import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faShapes } from "@fortawesome/pro-solid-svg-icons/faShapes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  EntityTypeSupportingGroups,
} from "@mapmycustomers/shared/types/entity";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";

import EntityIconTabs from "@app/component/EntityIconTabs";
import useMapView from "@app/component/view/utils/useMapView";
import MapMode from "@app/scene/map/enums/MapMode";
import { showPinSettings } from "@app/scene/map/store/actions";
import { showUpdatePinLegendDrawer } from "@app/scene/map/store/pinLegends/actions";
import { getGroupEntityType, getMapMode, getTerritoryMode } from "@app/scene/map/store/selectors";
import TerritoryModeState from "@app/scene/map/store/territoryMode/TerritoryModeState";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { getAllShapeLegends } from "@app/store/pinLegends";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import LegendCard, { BaseLegendCardProps } from "../LegendCard";

import EntityShapeKeyTab from "./components/EntityShapeKeyTab";
import RefreshShapePinLegend from "./components/RefreshShapePinLegend";
import styles from "./ShapeKeyLegendCard.module.scss";

interface Props extends BaseLegendCardProps {
  groupEntityType?: EntityTypeSupportingGroups;
  isReadOnlyMember: boolean;
  mapMode?: MapMode;
  onEditPinLegend?: (pinLegend: PinLegend) => void;
  onSetShowPinLegends: typeof showPinSettings;
  pinLegends: PinLegend[];
  territoryMode: TerritoryModeState["mode"];
}

const ShapeKeyLegendCard: React.FC<Props> = ({
  groupEntityType,
  isReadOnlyMember,
  mapMode,
  onEditPinLegend,
  onSetShowPinLegends,
  pinLegends,
  territoryMode,
  ...props
}) => {
  const analytics = useExtendedAnalytics("Shape Key", useAnalytics());
  const intl = useIntl();

  const viewingTerritory = mapMode === MapMode.TERRITORIES && territoryMode === "view";
  const viewingGroup = mapMode === MapMode.GROUPS;

  const [tab, setTab] = useState<EntityTypesSupportedByMapsPage>(() =>
    viewingGroup ? groupEntityType ?? EntityType.COMPANY : EntityType.COMPANY
  );
  const [title, setTitle] = useState<string | undefined>();

  const {
    viewState: { shapeKey, visibleEntities },
  } = useMapView();

  const pinLegend = useMemo(() => {
    return shapeKey?.[tab]
      ? pinLegends.find(({ id, entity }) => entity === tab && id === shapeKey[tab])
      : undefined;
  }, [shapeKey, pinLegends, tab]);

  const handleEditClick = useCallback(() => {
    if (pinLegend) {
      onEditPinLegend?.(pinLegend);
    }
  }, [onEditPinLegend, pinLegend]);

  const isEntityTypeDisabled = useCallback(
    (type: EntityTypesSupportedByMapsPage) => {
      if (viewingGroup) {
        return type !== groupEntityType;
      }
      return !visibleEntities.includes(type);
    },
    [groupEntityType, viewingGroup, visibleEntities]
  );

  const handleSelect = useCallback(
    (type) => {
      setTab(type);
      analytics.clicked([getEntityTypeAnalyticsName(type, false, true)]);
    },
    [setTab, analytics]
  );

  const handleManageButton = useCallback(() => {
    onSetShowPinLegends(true);
  }, [onSetShowPinLegends]);

  return (
    <LegendCard
      {...props}
      icon={<FontAwesomeIcon icon={faShapes} />}
      manageable
      manageButtonProps={{
        disabled: viewingTerritory || viewingGroup,
        tooltip: viewingTerritory
          ? intl.formatMessage({
              id: "map.layer.shapeKey.legend.manage.territory",
              defaultMessage: "Cannot be changed in territory view mode",
              description: "Tooltip for Manage button shown when map is in View Territory mode",
            })
          : viewingGroup
          ? intl.formatMessage({
              id: "map.layer.shapeKey.legend.manage.group",
              defaultMessage: "Cannot be changed in group view mode",
              description: "Tooltip for Manage button shown when map is in Group mode",
            })
          : undefined,
      }}
      onManage={handleManageButton}
      title={intl.formatMessage({
        id: "map.layer.shapeKey.legend",
        defaultMessage: "Shape Key",
        description: "Title of the Shape Key legend",
      })}
    >
      <EntityIconTabs<EntityTypesSupportedByMapsPage>
        activeEntityType={tab}
        className={styles.tabs}
        containerClassName={styles.tabsContainer}
        // only custom pin legends are editable
        editable={
          viewingTerritory || viewingGroup || isReadOnlyMember
            ? false
            : pinLegend
            ? !pinLegend.default
            : false
        }
        editProps={{
          tooltip: intl.formatMessage({
            id: "map.legends.shapeKey.actions.edit.tooltip",
            defaultMessage: "Edit Legend",
            description: "Tooltip of the Edit action in Legends card on the Map page",
          }),
        }}
        isEntityTypeDisabled={isEntityTypeDisabled}
        onEdit={handleEditClick}
        onSelect={handleSelect}
        title={intl.formatMessage(
          {
            id: "map.layer.shapeKey.legend.title",
            defaultMessage: "Shape by {title}",
            description: "Title of the Shape Key legend",
          },
          {
            title: viewingTerritory
              ? intl.formatMessage({
                  id: "map.layer.shapeKey.legend.title.territory",
                  defaultMessage: "Territory",
                  description: "Title of the Shape Key legend when viewing a territory",
                })
              : viewingGroup
              ? intl.formatMessage({
                  id: "map.layer.shapeKey.legend.title.group",
                  defaultMessage: "Group",
                  description: "Title of the Shape Key legend when viewing a group",
                })
              : title,
          }
        )}
      >
        <RefreshShapePinLegend entityType={tab} />
        <EntityShapeKeyTab entityType={tab} onSetTitle={setTitle} />
      </EntityIconTabs>
    </LegendCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  groupEntityType: getGroupEntityType(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  mapMode: getMapMode(state),
  pinLegends: getAllShapeLegends(state),
  territoryMode: getTerritoryMode(state),
});

const mapDispatchToProps = {
  onEditPinLegend: showUpdatePinLegendDrawer,
  onSetShowPinLegends: showPinSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShapeKeyLegendCard);
